/**
 * Sharing a document: its links, what has happened to each, and making another.
 *
 * Three things this screen is careful to say, because each is something a person
 * would otherwise assume wrongly.
 *
 * **The link is to this file.** Editing the design afterwards does not change what
 * the link shows. That is the point — a document sent is a record of what was sent
 * — but somebody who fixes a typo and expects the link to follow will be surprised,
 * so it is said before a link exists rather than discovered after.
 *
 * **Anyone with the link can open it.** An expiry and a password are offered on
 * the same form, not in a settings panel afterwards, because the moment somebody
 * decides to share is the moment they think about who might see it.
 *
 * **What a count means.** "12 views" from one person refreshing and from twelve
 * clients are different facts, so distinct visitors are shown beside views, and
 * wrong passwords are shown at all — somebody guessing is worth knowing about.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiError, api, helpUrl, type ReportSummary, type ShareView } from "../api.js";
import { reportFailure } from "../App.js";
import { Button, Chip, Field, Icon, Note, Sheet, ago, at, count, toast } from "../ui/index.js";

type Expiry = "never" | "1" | "7" | "30" | "date";

const EXPIRY_CHOICES: Array<{ value: Expiry; label: string }> = [
  { value: "never", label: "Never" },
  { value: "1", label: "In 24 hours" },
  { value: "7", label: "In 7 days" },
  { value: "30", label: "In 30 days" },
  { value: "date", label: "On a date…" }
];

/** The moment a choice means. A chosen date lasts to the end of that day, where the person is. */
function expiryFrom(choice: Expiry, date: string): string | null {
  if (choice === "never") return null;
  if (choice === "date") {
    if (!date) return null;
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y!, m! - 1, d!, 23, 59, 59).toISOString();
  }
  return new Date(Date.now() + Number(choice) * 86_400_000).toISOString();
}

/** Tomorrow, as a date input wants it — the earliest a chosen date can be. */
function tomorrow(): string {
  const t = new Date(Date.now() + 86_400_000);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
}

async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast("Link copied.", "good");
  } catch {
    // Clipboard access is refused in some embedded browsers and on plain http. The
    // link is on screen in a field that selects itself, so saying so is enough.
    toast("Your browser wouldn't let BroadPaper copy that. Select the link and copy it yourself.", "info");
  }
}

export function ShareSheet({ report, onClose, onChanged }: { report: ReportSummary; onClose: () => void; onChanged: () => void }) {
  const [shares, setShares] = useState<ShareView[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [fresh, setFresh] = useState<string | null>(null);
  const title = report.name.replace(/\.pdf$/i, "");

  const load = useCallback(async () => {
    try {
      const { shares: loaded } = await api.shares(report.id);
      setShares(loaded);
      // Nothing shared yet: the form is the screen.
      if (!loaded.length) setAdding(true);
    } catch (e) {
      reportFailure(e, "We couldn't load the links for this document.");
    }
  }, [report.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (share: ShareView) => {
    setShares((current) => (current ?? []).map((s) => (s.id === share.id ? share : s)));
    onChanged();
  };

  return (
    <Sheet title={`Share “${title}”`} onClose={onClose} wide>
      <div className="stack" style={{ gap: 16 }}>
        <p className="muted small">
          Anyone with a link can open this PDF and download it — no account needed. The link is to this file, so editing the design later doesn't change
          what it shows. <a href={helpUrl("/support/documents-and-sharing/#share-links")} target="_blank" rel="noreferrer">How sharing works</a>
        </p>

        {shares === null ? (
          <div className="skeleton" style={{ height: 120 }} />
        ) : (
          shares.map((share) => (
            <ShareCard
              key={share.id}
              share={share}
              highlighted={share.id === fresh}
              onChange={replace}
              onDeleted={() => {
                setShares((current) => (current ?? []).filter((s) => s.id !== share.id));
                onChanged();
              }}
            />
          ))
        )}

        {adding ? (
          <NewLink
            reportId={report.id}
            first={!shares?.length}
            onCancel={shares?.length ? () => setAdding(false) : undefined}
            onCreated={(share) => {
              setShares((current) => [share, ...(current ?? [])]);
              setFresh(share.id);
              setAdding(false);
              onChanged();
              void copy(share.url);
            }}
          />
        ) : shares ? (
          <div>
            <Button icon="plus" onClick={() => setAdding(true)}>
              Make another link
            </Button>
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

/* ------------------------------ a new link ------------------------------ */

function NewLink({ reportId, first, onCreated, onCancel }: { reportId: string; first: boolean; onCreated: (share: ShareView) => void; onCancel?: (() => void) | undefined }) {
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState<Expiry>("never");
  const [date, setDate] = useState("");
  const [protect, setProtect] = useState(false);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = protect && password.length < 6;
  const noDate = expiry === "date" && !date;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const { share } = await api.createShare(reportId, {
        label: label.trim() || null,
        expiresAt: expiryFrom(expiry, date),
        password: protect ? password : null
      });
      onCreated(share);
    } catch (e) {
      setError(e instanceof ApiError ? [e.message, e.hint].filter(Boolean).join(" ") : "We couldn't make that link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="share-card share-card--new">
      <div className="stack" style={{ gap: 14 }}>
        <strong>{first ? "Make a link" : "Another link"}</strong>

        <Field label="Who is it for?" id="share-label" hint="Optional, and only you see it — so you can tell your links apart.">
          <input id="share-label" className="input" value={label} maxLength={200} placeholder="Aldermead Ltd" onChange={(e) => setLabel(e.target.value)} />
        </Field>

        <div className="grid grid--2" style={{ gap: 12 }}>
          <Field label="Stops working" id="share-expiry">
            <select id="share-expiry" className="select" value={expiry} onChange={(e) => setExpiry(e.target.value as Expiry)}>
              {EXPIRY_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
          {expiry === "date" ? (
            <Field label="At the end of" id="share-date">
              <input id="share-date" className="input" type="date" min={tomorrow()} value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
          ) : null}
        </div>

        <label className="row" style={{ gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={protect} onChange={(e) => setProtect(e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--red)" }} />
          <span>Ask for a password</span>
        </label>
        {protect ? (
          <Field label="Password" id="share-password" hint="At least 6 characters. Send it separately from the link — a different message, or tell them." error={password && tooShort ? "At least 6 characters." : undefined}>
            <div className="row" style={{ gap: 6 }}>
              <input
                id="share-password"
                className="input"
                type={show ? "text" : "password"}
                value={password}
                autoComplete="new-password"
                maxLength={200}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button size="sm" variant="quiet" onClick={() => setShow(!show)}>
                {show ? "Hide" : "Show"}
              </Button>
            </div>
          </Field>
        ) : null}

        {error ? <Note tone="bad">{error}</Note> : null}

        <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
          {onCancel ? (
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          <Button variant="primary" icon="share" busy={busy} disabled={tooShort || noDate} onClick={() => void create()}>
            Make the link and copy it
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- a link -------------------------------- */

type Editing = null | "expiry" | "password" | "stop" | "delete";

function ShareCard({ share, highlighted, onChange, onDeleted }: { share: ShareView; highlighted: boolean; onChange: (share: ShareView) => void; onDeleted: () => void }) {
  const [editing, setEditing] = useState<Editing>(null);
  const [busy, setBusy] = useState(false);
  const [expiry, setExpiry] = useState<Expiry>("7");
  const [date, setDate] = useState("");
  const [password, setPassword] = useState("");
  const [activity, setActivity] = useState(false);

  const working = share.state === "active";

  const update = async (body: Parameters<typeof api.updateShare>[1], done: string) => {
    setBusy(true);
    try {
      const { share: updated } = await api.updateShare(share.id, body);
      onChange(updated);
      setEditing(null);
      setPassword("");
      toast(done, "good");
    } catch (e) {
      reportFailure(e, "We couldn't change that link.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteShare(share.id);
      onDeleted();
      toast("Link deleted.", "good");
    } catch (e) {
      setBusy(false);
      reportFailure(e, "We couldn't delete that link.");
    }
  };

  return (
    <div className={`share-card${highlighted ? " share-card--fresh" : ""}${working ? "" : " share-card--off"}`}>
      <div className="spread" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <div className="stack" style={{ gap: 4, minWidth: 0 }}>
          <strong className="truncate">{share.label || `Link made ${at(share.createdAt)}`}</strong>
          <span className="small muted">
            {share.state === "stopped"
              ? `Stopped ${at(share.stoppedAt)}`
              : share.state === "expired"
                ? `Expired ${at(share.expiresAt)}`
                : share.expiresAt
                  ? `Stops working ${at(share.expiresAt)}`
                  : "Doesn't expire"}
          </span>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {share.hasPassword ? (
            <Chip>
              <Icon name="lock" className="chip__icon" /> Password
            </Chip>
          ) : null}
          <Chip tone={working ? "good" : share.state === "expired" ? "ask" : undefined}>{working ? "Working" : share.state === "expired" ? "Expired" : "Stopped"}</Chip>
        </div>
      </div>

      {working ? (
        <div className="row" style={{ gap: 6 }}>
          <input className="input input--sm mono" readOnly value={share.url} onFocus={(e) => e.currentTarget.select()} aria-label="The link" />
          <Button size="sm" icon="copy" onClick={() => void copy(share.url)}>
            Copy
          </Button>
        </div>
      ) : null}

      <dl className="share-stats">
        <div>
          <dt>Views</dt>
          <dd>{count(share.views)}</dd>
        </div>
        <div>
          <dt>Downloads</dt>
          <dd>{count(share.downloads)}</dd>
        </div>
        <div>
          <dt title="Counted by browser and network, without recording either">Visitors</dt>
          <dd>{count(share.visitors)}</dd>
        </div>
        <div>
          <dt>Last opened</dt>
          <dd className="small">{share.lastViewedAt ? ago(share.lastViewedAt) : "Not yet"}</dd>
        </div>
      </dl>

      {share.passwordFailures > 0 ? (
        <p className="small" style={{ color: "var(--amber)" }}>
          <Icon name="warn" className="inline-icon" /> {count(share.passwordFailures)} wrong password{share.passwordFailures === 1 ? "" : "s"} entered
          {share.firstViewedAt ? "" : " and never opened"}.
        </p>
      ) : null}

      {share.recent.length ? (
        <div>
          <button type="button" className="link-button small" onClick={() => setActivity(!activity)} aria-expanded={activity}>
            {activity ? "Hide activity" : "Recent activity"}
          </button>
          {activity ? (
            <ul className="share-activity">
              {share.recent.map((event, i) => (
                <li key={i}>
                  <span className={event.kind === "denied" ? "share-activity__bad" : ""}>
                    {event.kind === "view" ? "Opened" : event.kind === "download" ? "Downloaded" : "Wrong password"}
                  </span>
                  <span className="muted">{event.device ?? "Unknown browser"}</span>
                  <span className="muted">{ago(event.at)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {editing === "expiry" ? (
        <div className="share-edit">
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <select className="select" style={{ width: "auto" }} value={expiry} onChange={(e) => setExpiry(e.target.value as Expiry)} aria-label="Stops working">
              {EXPIRY_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.value === "never" ? "Never stops working" : c.label}
                </option>
              ))}
            </select>
            {expiry === "date" ? <input className="input" style={{ width: "auto" }} type="date" min={tomorrow()} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date" /> : null}
            <Button
              size="sm"
              variant="primary"
              busy={busy}
              disabled={expiry === "date" && !date}
              onClick={() => void update({ expiresAt: expiryFrom(expiry, date) }, working ? "Expiry changed." : "Expiry changed. The same link works again.")}
            >
              Save
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : editing === "password" ? (
        <div className="share-edit">
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <input
              className="input"
              style={{ flex: "1 1 180px" }}
              type="password"
              autoComplete="new-password"
              placeholder={share.hasPassword ? "A new password" : "At least 6 characters"}
              value={password}
              maxLength={200}
              onChange={(e) => setPassword(e.target.value)}
              aria-label="Password"
            />
            <Button size="sm" variant="primary" busy={busy} disabled={password.length < 6} onClick={() => void update({ password }, share.hasPassword ? "Password changed." : "Password added.")}>
              {share.hasPassword ? "Change" : "Add"}
            </Button>
            {share.hasPassword ? (
              <Button size="sm" busy={busy} onClick={() => void update({ password: null }, "Password removed. Anyone with the link can open it.")}>
                Remove password
              </Button>
            ) : null}
            <Button size="sm" variant="quiet" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : editing === "stop" ? (
        <div className="share-edit share-edit--warn">
          <p className="small">
            The link stops working straight away. Anybody who opens it is told it is no longer available. Its counts stay here, and you can make a new link
            whenever you like.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <Button size="sm" variant="danger" busy={busy} onClick={() => void update({ stop: true }, "Stopped. That link no longer works.")}>
              Stop sharing
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setEditing(null)}>
              Keep it working
            </Button>
          </div>
        </div>
      ) : editing === "delete" ? (
        <div className="share-edit share-edit--warn">
          <p className="small">{working ? "The link stops working and its counts and activity are deleted." : "Its counts and activity are deleted."} This can't be undone.</p>
          <div className="row" style={{ gap: 8 }}>
            <Button size="sm" variant="danger" busy={busy} onClick={() => void remove()}>
              Delete the link
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setEditing(null)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: 4, flexWrap: "wrap" }}>
          {/*
            An expired link can be given a new date and works again at the same
            address, so the person it was sent to needs nothing new. A stopped link
            cannot: the owner turned it off, and it stays off.
          */}
          {share.state !== "stopped" ? (
            <Button size="sm" variant="quiet" onClick={() => setEditing("expiry")}>
              {working ? "Change expiry" : "Give it a new expiry"}
            </Button>
          ) : null}
          {working ? (
            <>
              <Button size="sm" variant="quiet" onClick={() => setEditing("password")}>
                {share.hasPassword ? "Change password" : "Add a password"}
              </Button>
              <Button size="sm" variant="quiet" onClick={() => setEditing("stop")}>
                Stop sharing
              </Button>
            </>
          ) : null}
          <Button size="sm" variant="quiet" icon="trash" onClick={() => setEditing("delete")}>
            Delete link
          </Button>
        </div>
      )}
    </div>
  );
}
